import { useCallback } from 'react';
import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/core';
import { useClientTranslation } from '@/lib/core/i18n';

export interface InterestToggleProps {
  isSelected: boolean;
  onToggle: () => void;
  /** Icon size in px. Defaults to 16. */
  size?: number;
  className?: string;
  disabled?: boolean;
}

/**
 * Toggle button rendering an outline / solid bookmark — mirrors `HeartToggle` for the
 * "add to interests" affordance (as opposed to favoriting an item).
 */
export function InterestToggle({
  isSelected,
  onToggle,
  size = 16,
  className,
  disabled,
}: InterestToggleProps) {
  const t = useClientTranslation();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled) return;
      onToggle();
    },
    [disabled, onToggle]
  );

  return (
    <button
      type="button"
      aria-pressed={isSelected}
      aria-label={isSelected ? t('App.Labels.RemovePreference') : t('App.Labels.AddPreference')}
      disabled={disabled}
      onClick={handleClick}
      className={cn(
        'text-jncquoi-neutral-1000 inline-flex items-center justify-center',
        'disabled:cursor-not-allowed disabled:opacity-40',
        className
      )}
    >
      <Icon name={isSelected ? 'bookmark-solid' : 'bookmark'} width={size} height={size} />
    </button>
  );
}
